import { useState, useEffect } from "react";
import { getNextMonth, getPreviousMonth } from "@/utils/calendar";
import { addMonths, subMonths } from "date-fns";

/**
 * Custom hook for managing calendar date state and navigation
 * Supports development mode with the query limits bypass feature
 */
export const useCalendarDate = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    minDate: subMonths(new Date(), 1),
    maxDate: addMonths(new Date(), 1)
  });

  /**
   * Check if query limits should be disabled (development mode)
   * When true, no date range restrictions are applied
   */
  const disableQueryLimits = import.meta.env.VITE_DISABLE_QUERY_LIMITS === 'true';

  // Update the available date range based on whether limits are disabled
  useEffect(() => {
    if (disableQueryLimits) {
      // In development mode with disabled limits, allow viewing any date
      setDateRange({
        minDate: subMonths(new Date(), 24), // 2 years back
        maxDate: addMonths(new Date(), 12)  // 1 year ahead
      });
      console.log('useCalendarDate: Extended date range applied for development');
    } else {
      // In production mode, apply the normal date range for free users
      setDateRange({
        minDate: subMonths(new Date(), 1),
        maxDate: addMonths(new Date(), 1)
      });
    }
  }, [disableQueryLimits]);

  const handlePrevMonth = () => {
    // With disabled limits, allow navigation to any month
    // Otherwise, respect the minDate constraint
    if (disableQueryLimits || subMonths(currentDate, 1) >= dateRange.minDate) {
      setCurrentDate(getPreviousMonth);
    }
  };

  const handleNextMonth = () => {
    // With disabled limits, allow navigation to any month
    // Otherwise, respect the maxDate constraint
    if (disableQueryLimits || addMonths(currentDate, 1) <= dateRange.maxDate) {
      setCurrentDate(getNextMonth);
    }
  };

  return {
    currentDate,
    handlePrevMonth,
    handleNextMonth,
    dateRange,
    disableQueryLimits
  };
};